// Angular
import { Injectable } from '@angular/core';
import { get as _get, has as _has } from 'lodash';

// Misc
import {
  Claim,
  ClaimDetailServiceRequestModel,
  MonetaryTransaction
} from '@amfam/claim/data-access';
// Services
import { ClaimUtilService } from '@amfam/claim/shared/utility';

import { ClaimActivity, ClaimGeneralDataModel, ClaimProgress } from '../models';
import { ClaimDetailConstant } from '../models/claim-detail-constants';

@Injectable({ providedIn: 'root' })
export class ClaimStatusService {
  constructor(private claimUtilService: ClaimUtilService) {}

  // JP: Pulls information from Claim Detail to setup progress indicator in Claim Status page
  public buildProgressData(claim: Claim, claimType: ClaimDetailConstant): ClaimProgress {
    const claimProgress: ClaimProgress = {
      claimSteps: this.claimUtilService.buildIndicatorStatus(claim),
      claimProgress: claim.claimProgress.toUpperCase(),
      lossDescription: _has(claim, 'lossCause') ? claim.lossCause[0].description : '',
      lossDetailDescription: _has(claim, '')
        ? claim.claimDetail.detailedLossCauseCode.description
        : ''
    };
    if (claimType === ClaimDetailConstant.PROPERTY) {
      claimProgress.address = {
        addressLine1: _get(claim, 'lossLocationAddress.addr1', ''),
        addressLine2: _get(claim, 'lossLocationAddress.addr2', ''),
        city: _get(claim, 'lossLocationAddress.city', ''),
        state: _get(claim, 'lossLocationAddress.state', ''),
        addressType: _get(claim, 'lossLocationAddress.addressType', ''),
        zipCode: _get(claim, 'lossLocationAddress.zip', ''),
        zipCode5: _get(claim, 'lossLocationAddress.zip5', ''),
        zipCode4: _get(claim, 'lossLocationAddress.zip4', ''),
        getStringifiedAddress: () => ''
      };
      return claimProgress;
    } else if (claimType === ClaimDetailConstant.AUTO) {
      claimProgress.vehicle = claim.autoClaimDetails;
      return claimProgress;
    }
    return claimProgress;
  }

  // JP: Pulls info to build the Summary accordion in Claim Status page
  public buildGeneralData(claim: Claim): ClaimGeneralDataModel {
    return {
      dateFiled: this.claimUtilService.formatDate(
        _get(claim, 'reportedBy.reportedOn').split('T')[0]
      ),
      incidentDate: this.claimUtilService.formatDate(
        _get(claim, 'lossDate').toString().split('T')[0]
      ),
      timeOfIncident: this.claimUtilService.formatTime(
        _get(claim, 'lossDate').toString().slice(0, 19)
      ),
      vehicle: _get(claim, 'autoClaimDetails[0]', null),
      policyNumber: _get(claim, 'policyNumber', null),
      lossDescription: _get(claim, 'lossCauses[0].description', null),
      lossDetailDescription: _get(claim, 'claimDetail.detailedLossCause.description', null),
      claimDescription: _get(claim, 'claimDescription', null),
      lossLocationAddress: _get(claim, 'lossLocationAddress', null),
      claimProgress: _get(claim, 'claimProgress', null)
    };
  }

  // JP: Looping through monetary transactions, builds info for payments accordion of claim status
  // If claim is of type auto, also takes arguements and build activity info for rental and repair
  // accordions
  public buildActivityData(
    claim: Claim,
    paymentsData: MonetaryTransaction[],
    claimType: ClaimDetailConstant,
    towingAndRepairData?: ClaimDetailServiceRequestModel[]
  ): ClaimActivity[] {
    let claimActivity: ClaimActivity[] = new Array<ClaimActivity>();

    // Loop through monetary transactions and setup array for Payments accordion for all claims
    if (_get(claim, 'claimDetail.monetaryTransactions')) {
      for (let i = 0; i < claim.claimDetail.monetaryTransactions.length; i++) {
        if (
          claim.claimDetail.monetaryTransactions[i].typeOfTransactionCode.toUpperCase() ===
            'PAYMENT' &&
          claim.claimDetail.monetaryTransactions[i].typeOfPaymentPaidToCode === 'FIRST PARTY' &&
          claim.claimDetail.monetaryTransactions[i].typeOfCostCode.toUpperCase() !== 'AOEXPENSE' &&
          claim.claimDetail.monetaryTransactions[i].typeOfTransactionStatusCode.toUpperCase() ===
            'ISSUED'
        ) {
          paymentsData.push(claim.claimDetail.monetaryTransactions[i]);
          claimActivity.push({
            date: this.claimUtilService.formatDate(
              claim.claimDetail.monetaryTransactions[i].transactedOn
            ),
            message:
              '$' +
              (num => _get(claim.claimDetail.monetaryTransactions[num], 'amount', '').toString())(
                i
              ) +
              ' payment to ' +
              (num =>
                _get(claim.claimDetail.monetaryTransactions[num], 'typeOfPaymentPaidToCode', '')
                  .toString()
                  .toUpperCase() === 'FIRST PARTY'
                  ? _get(claim.claimDetail.monetaryTransactions[num], 'payee', '').toString()
                  : '')(i)
          });
        }
      }
    }

    // Add initial elements to claimActivity
    claimActivity = [
      ...claimActivity,
      {
        date:
          _get(claim, 'claimDetail.initialPhoneCallCompletedIndicator', null) === 'true'
            ? this.claimUtilService.formatDate(claim.claimDetail.initialPhoneCallCompletedon)
            : '',
        message: 'Initial call'
      },
      {
        date: this.claimUtilService.formatDate(claim.openDate.toString()),
        message: 'Claim opened'
      }
    ];

    // If type is of auto, we also need to build repair and rental service info for
    // respective accordions
    if (claimType === ClaimDetailConstant.AUTO) {
      const serviceDates = { towingInfoDate: '', rentalCarInfoDate: '', shopInfoDate: '' };

      if (
        _get(claim, 'claimDetail.serviceRequests') &&
        claim.claimDetail.serviceRequests.length > 0
      ) {
        for (let i = 0; i < claim.claimDetail.serviceRequests.length; i++) {
          if (
            claim.claimDetail.serviceRequests[i].typeOfServiceRequestCode.toUpperCase() ===
            ClaimDetailConstant.DREAM_REPAIR_PROGRAM
          ) {
            towingAndRepairData.push(claim.claimDetail.serviceRequests[i]);
            serviceDates.shopInfoDate = claim.claimDetail.serviceRequests[i].serviceRequestedOn;
            continue;
          }
          if (
            claim.claimDetail.serviceRequests[i].typeOfServiceRequestCode.toUpperCase() ===
            ClaimDetailConstant.RENTAL
          ) {
            serviceDates.rentalCarInfoDate =
              claim.claimDetail.serviceRequests[i].serviceRequestedOn;
            continue;
          }
          if (
            claim.claimDetail.serviceRequests[i].typeOfServiceRequestCode.toUpperCase() ===
            ClaimDetailConstant.TOWING
          ) {
            towingAndRepairData.push(claim.claimDetail.serviceRequests[i]);
            serviceDates.towingInfoDate = claim.claimDetail.serviceRequests[i].serviceRequestedOn;
            continue;
          }
        }

        // Add rental/repair info to existing claim activity array
        claimActivity = [
          ...claimActivity,
          {
            date: this.claimUtilService.formatDate(serviceDates.shopInfoDate),
            message: 'Shop info received'
          },
          {
            date: this.claimUtilService.formatDate(serviceDates.rentalCarInfoDate),
            message: 'Rental car info received'
          },
          {
            date: this.claimUtilService.formatDate(serviceDates.towingInfoDate),
            message: 'Towing info received'
          }
        ];
      }
    }

    return claimActivity.filter(claimActivityItem => claimActivityItem.date);
  }
}
